import ExchangeRouter from "sdk/abis/ExchangeRouter.json";
import GmxV2Adapter from "sdk/abis/GmxV2Adapter.json";
import ProxyFactory from "sdk/abis/ProxyFactory.json";
import { getContract } from "config/contracts";
import { NATIVE_TOKEN_ADDRESS, convertTokenAddress } from "config/tokens";
import { SetPendingFundingFeeSettlement, SetPendingOrder, SetPendingPosition } from "context/SyntheticsEvents";
import { TokensData, convertToContractPrice } from "domain/synthetics/tokens";
import { Token } from "domain/tokens";
import { Signer, ethers } from "ethers";
import { callContract } from "lib/contracts";
import { getPositionKey } from "../positions";
import { applySlippageToMinOut, applySlippageToPrice } from "../trade";
import { PriceOverrides, simulateExecuteTxn } from "./simulateExecuteTxn";
import { DecreasePositionSwapType, OrderType } from "./types";
import { isMarketOrderType, getPendingOrderFromParams } from "./utils";
import { t } from "@lingui/macro";
import { Subaccount } from "context/SubaccountContext/SubaccountContext";
import { getSubaccountRouterContract } from "../subaccount/getSubaccountContract";
import { UI_FEE_RECEIVER_ACCOUNT } from "config/ui";
import { OrderMetricId } from "lib/metrics";
import { prepareOrderTxn } from "./prepareOrderTxn";

const { ZeroAddress } = ethers;

export type DecreaseOrderParams = {
  account: string;
  marketAddress: string;
  initialCollateralAddress: string;
  initialCollateralDeltaAmount: bigint;
  swapPath: string[];
  receiveTokenAddress: string;
  sizeDeltaUsd: bigint;
  sizeDeltaInTokens: bigint;
  acceptablePrice: bigint;
  triggerPrice: bigint | undefined;
  minOutputUsd: bigint;
  isLong: boolean;
  decreasePositionSwapType: DecreasePositionSwapType;
  orderType: OrderType.MarketDecrease | OrderType.LimitDecrease | OrderType.StopLossDecrease;
  executionFee: bigint;
  allowedSlippage: number;
  skipSimulation?: boolean;
  referralCode?: string;
  indexToken: Token;
  tokensData: TokensData;
  autoCancel: boolean;
};

export type DecreaseOrderCallbacks = {
  setPendingTxns: (txns: any) => void;
  setPendingOrder?: SetPendingOrder;
  setPendingPosition?: SetPendingPosition;
  setPendingFundingFeeSettlement?: SetPendingFundingFeeSettlement;
};

export async function createDecreaseOrderTxn(
  chainId: number,
  signer: Signer,
  subaccount: Subaccount,
  params: DecreaseOrderParams | DecreaseOrderParams[],
  callbacks: DecreaseOrderCallbacks,
  metricId?: OrderMetricId
) {
  const ps = Array.isArray(params) ? params : [params];

  // derive which proxyAddress to use 
  // by checking the positionKey?
  // set account to said proxyAddress

  const shouldApplySlippage = isMarketOrderType(ps[0].orderType);

  const acceptablePrice = shouldApplySlippage
    ? applySlippageToPrice(ps[0].allowedSlippage, ps[0].acceptablePrice, false, ps[0].isLong)
    : ps[0].acceptablePrice;

  const minOutputAmount = shouldApplySlippage
    ? applySlippageToMinOut(ps[0].allowedSlippage, ps[0].minOutputUsd)
    : ps[0].minOutputUsd;
  const gmxV2Adapter = new ethers.Contract(getContract(chainId, "GmxV2Adapter"), GmxV2Adapter.abi, signer);
  const proxyFactory = new ethers.Contract(getContract(chainId, "ProxyFactory"), ProxyFactory.abi, signer);
  
  const exchangeRouter = new ethers.Contract(getContract(chainId, "ExchangeRouter"), ExchangeRouter.abi, signer);
  const router = subaccount ? getSubaccountRouterContract(chainId, subaccount.signer) : exchangeRouter;

  const orderVaultAddress = getContract(chainId, "OrderVault");
  const totalWntAmount = ps.reduce((acc, p) => acc + p.executionFee, 0n);
  const account = ps[0].account;
  // Update all accounts to their proxy addresses first
  try {
    await Promise.all(ps.map(async (p) => {
      const proxyAddress = await proxyFactory.getProxyAddress.staticCall(2n, account, p.initialCollateralAddress, p.marketAddress, p.isLong);
      console.log("Proxy address:", "Lalalalalala");

    }));
  } catch (error) {
    console.error("Error getting proxy address:", error);
    throw error;
  }

  // const encodedPayload = createDecreaseEncodedPayload({
  //   router,
  //   orderVaultAddress,
  //   ps,
  //   subaccount,
  //   mainAccountAddress: account,
  //   chainId,
  // });

  const winEncodedPayload = winDecreaseEncodedPayload({
    proxyFactory,
    gmxV2Adapter,
    ps,
    subaccount,
    mainAccountAddress: account,
    chainId,
  });

  const simWinEncodedPayload = winDecreaseEncodedPayload({
    proxyFactory,
    gmxV2Adapter,
    ps,
    subaccount,
    mainAccountAddress: account,
    chainId,
  });

  // const simulationEncodedPayload = createDecreaseEncodedPayload({
  //   router: exchangeRouter,
  //   orderVaultAddress,
  //   ps,
  //   subaccount: null,
  //   mainAccountAddress: account,
  //   chainId,
  // });

  const simulationPromise = Promise.all(
    ps.map(async (p) => {
      if (subaccount && callbacks.setPendingOrder) {
        callbacks.setPendingOrder(getPendingOrderFromParams(chainId, "create", p));
      }

      if (!p.skipSimulation) {
        const primaryPriceOverrides: PriceOverrides = {};
        if (p.triggerPrice != undefined) {
          primaryPriceOverrides[p.indexToken.address] = {
            minPrice: p.triggerPrice,
            maxPrice: p.triggerPrice,
          };
        }
        await simulateExecuteTxn(chainId, {
          account,
          primaryPriceOverrides,
          createMulticallPayload: simWinEncodedPayload,// simulationEncodedPayload,
          value: 0n,//totalWntAmount,
          method: "simulateProxyFunctionCall",
          tokensData: p.tokensData,
          errorTitle: t`Order error.`,
          metricId,
        });
      }
    })
  );

  const { gasLimit, gasPriceData, customSignersGasLimits, customSignersGasPrices, bestNonce } = await prepareOrderTxn(
    chainId,
    proxyFactory,
    "multicall",
    [winEncodedPayload],
    0n,//totalWntAmount,
    subaccount?.customSigners,
    simulationPromise,
    metricId
  );

  const txnCreatedAt = Date.now();

  if (!signer.provider) throw new Error("No provider found"); //@note do we need this?

  await callContract(chainId, proxyFactory, "multicall", [winEncodedPayload], {
    value: 0n,
    hideSentMsg: true,
    hideSuccessMsg: true,
    customSigners: subaccount?.customSigners,
    customSignersGasLimits,
    customSignersGasPrices,
    metricId,
    gasLimit,
    gasPriceData,
    bestNonce,
    setPendingTxns: callbacks.setPendingTxns,
    simulationContract: proxyFactory,
    simulationMethod: "multicall",
    simulationParams: [winEncodedPayload],
    simulationValue: 0n,
  });

  const txnCreatedAtBlock = await signer.provider.getBlockNumber();

  ps.forEach((p) => {
    if (isMarketOrderType(p.orderType)) {
      if (callbacks.setPendingPosition) {
        callbacks.setPendingPosition(getPendingPositionFromParams(txnCreatedAt, txnCreatedAtBlock, p));
      }
    }

    if (!subaccount && callbacks.setPendingOrder) {
      callbacks.setPendingOrder(getPendingOrderFromParams(chainId, "create", p));
    }
  });

  if (callbacks.setPendingFundingFeeSettlement) {
    callbacks.setPendingFundingFeeSettlement({
      orders: ps.map((p) => getPendingOrderFromParams(chainId, "create", p)),
      positions: ps.map((p) => getPendingPositionFromParams(txnCreatedAt, txnCreatedAtBlock, p)),
    });
  }
}

function getPendingPositionFromParams(txnCreatedAt: number, txnCreatedAtBlock: number, p: DecreaseOrderParams) {
  const positionKey = getPositionKey(p.account, p.marketAddress, p.initialCollateralAddress, p.isLong);
  return {
    isIncrease: false,
    positionKey,
    collateralDeltaAmount: p.initialCollateralDeltaAmount,
    sizeDeltaUsd: p.sizeDeltaUsd,
    sizeDeltaInTokens: p.sizeDeltaInTokens,
    updatedAt: txnCreatedAt,
    updatedAtBlock: BigInt(txnCreatedAtBlock),
  };
}

function winDecreaseOrderParams({
  gmxV2Adapter,
  p,
  acceptablePrice,
  initialCollateralTokenAddress,
  subaccount,
  isNativeReceive,
  minOutputUsd,
}: {
  gmxV2Adapter: ethers.Contract;
  p: DecreaseOrderParams;
  acceptablePrice: bigint;
  initialCollateralTokenAddress: string;
  subaccount: Subaccount | null;
  isNativeReceive: boolean;
  minOutputUsd: bigint;
}) {
  // const swapP = string[]
  const params = {
    swapPath: [p.marketAddress], //p.swapPath, @note alter this later?
    initialCollateralAmount: p.initialCollateralDeltaAmount,
    tokenOutMinAmount: minOutputUsd, //@note what is this? used LibGmx L208, can be removed
    borrowCollateralAmount: 0n, 
    sizeDeltaUsd: p.sizeDeltaUsd,
    triggerPrice: convertToContractPrice(p.triggerPrice ?? 0n, p.indexToken.decimals),
    acceptablePrice: convertToContractPrice(acceptablePrice, p.indexToken.decimals),
    executionFee: p.executionFee,
    callbackGasLimit: 4000000n,
    orderType: p.orderType,
  };
  return {
    projectId: 2n,
    collateralToken: initialCollateralTokenAddress,
    assetToken: p.marketAddress,
    isLong: p.isLong,
    referralCode: p.referralCode || ethers.ZeroHash,
    value: 0n,
    proxyCallData: gmxV2Adapter.interface.encodeFunctionData("placeOrder", [params]),
  };
}

export function winDecreaseEncodedPayload({
  proxyFactory,
  gmxV2Adapter,
  ps,
  subaccount,
  mainAccountAddress,
  chainId,
}: {
  proxyFactory: ethers.Contract;
  gmxV2Adapter: ethers.Contract;
  ps: DecreaseOrderParams[];
  subaccount: Subaccount;
  mainAccountAddress: string;
  chainId: number;
}) {
  const multicall = ps.map((p) => {
      const isNativeReceive = p.receiveTokenAddress === NATIVE_TOKEN_ADDRESS;
      const initialCollateralTokenAddress = convertTokenAddress(chainId, p.initialCollateralAddress, "wrapped");
      const shouldApplySlippage = isMarketOrderType(p.orderType);
      const acceptablePrice = shouldApplySlippage
        ? applySlippageToPrice(p.allowedSlippage, p.acceptablePrice, false, p.isLong)
        : p.acceptablePrice;
      const minOutputAmount = shouldApplySlippage
        ? applySlippageToMinOut(p.allowedSlippage, p.minOutputUsd)
        : p.minOutputUsd;
      const orderParams = winDecreaseOrderParams({
        gmxV2Adapter,
        p,
        acceptablePrice,
        initialCollateralTokenAddress,
        subaccount,
        isNativeReceive,
        minOutputUsd:minOutputAmount,
      });

      return {
        method: "proxyFunctionCall",
        params: [orderParams]
      };
    });

  return multicall.filter(Boolean).map((call) => proxyFactory.interface.encodeFunctionData(call!.method, call!.params));
}


export function createDecreaseEncodedPayload({
  router,
  orderVaultAddress,
  ps,
  subaccount,
  mainAccountAddress,
  chainId,
}: {
  router: ethers.Contract;
  orderVaultAddress: string;
  ps: DecreaseOrderParams[];
  subaccount: Subaccount;
  mainAccountAddress: string;
  chainId: number;
}) {
  const multicall = [
    ...ps.flatMap((p) => {
      const isNativeReceive = p.receiveTokenAddress === NATIVE_TOKEN_ADDRESS;

      const initialCollateralTokenAddress = convertTokenAddress(chainId, p.initialCollateralAddress, "wrapped");

      const shouldApplySlippage = isMarketOrderType(p.orderType);

      const acceptablePrice = shouldApplySlippage
        ? applySlippageToPrice(p.allowedSlippage, p.acceptablePrice, false, p.isLong)
        : p.acceptablePrice;

      const minOutputAmount = shouldApplySlippage
        ? applySlippageToMinOut(p.allowedSlippage, p.minOutputUsd)
        : p.minOutputUsd;
      const orderParams = {
        addresses: {
          cancellationReceiver: ethers.ZeroAddress,
          receiver: p.account,
          initialCollateralToken: initialCollateralTokenAddress,
          callbackContract: ZeroAddress,
          market: p.marketAddress,
          swapPath: p.swapPath,
          uiFeeReceiver: UI_FEE_RECEIVER_ACCOUNT ?? ethers.ZeroAddress,
        },
        numbers: {
          sizeDeltaUsd: p.sizeDeltaUsd,
          initialCollateralDeltaAmount: p.initialCollateralDeltaAmount,
          triggerPrice: convertToContractPrice(p.triggerPrice ?? 0n, p.indexToken.decimals),
          acceptablePrice: convertToContractPrice(acceptablePrice, p.indexToken.decimals),
          executionFee: p.executionFee,
          callbackGasLimit: 0n,
          validFromTime: 0n,
          minOutputAmount,
        },
        orderType: p.orderType,
        decreasePositionSwapType: p.decreasePositionSwapType,
        isLong: p.isLong,
        shouldUnwrapNativeToken: isNativeReceive,

        autoCancel: p.autoCancel,
        referralCode: p.referralCode || ethers.ZeroHash,
      };

      return [
        { method: "sendWnt", params: [orderVaultAddress, p.executionFee] },
        {
          method: "createOrder",
          params: subaccount ? [mainAccountAddress, orderParams] : [orderParams],
        },
      ];
    }),
  ];

  return multicall.filter(Boolean).map((call) => router.interface.encodeFunctionData(call!.method, call!.params));
}
